import React, { useEffect, useState } from 'react';

import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { attorneysService } from '../../../services/attorneysService';
import { homepageSliderService } from '../../../services/homepageSliderService';
import { practiceAreasService } from '../../../services/practiceAreasService';
import { publicationsService } from '../../../services/publicationsService';
import { storageService } from '../../../services/storageService';

const Dashboard = () => {
	const [stats, setStats] = useState({
		totalSlides: 0,
		totalImages: 0,
		totalPublications: 0,
		totalPracticeAreas: 0,
		totalAttorneys: 0,
	});
	const [loading, setLoading] = useState(true);
	const [systemHealth, setSystemHealth] = useState({
		database: 'unknown',
		storage: 'unknown',
		api: 'unknown',
	});
	const [recentActivity, setRecentActivity] = useState([]);

	useEffect(() => {
		loadDashboardData();
		// No automatic refresh - only manual triggers
	}, []);

	const loadDashboardData = async () => {
		try {
			setLoading(true);

			// Load stats from services
			const [slidesResult, practiceAreasResult, publicationsResult, attorneysResult] =
				await Promise.allSettled([
					homepageSliderService.getSlides(),
					practiceAreasService.getAllPracticeAreas(),
					publicationsService.getPublications(true), // admin mode
					attorneysService.getAllAttorneys(),
				]);

			// Load images count
			let imagesCount = 0;
			try {
				const imagesResult = await storageService.listImages('images');
				imagesCount = imagesResult?.data?.length || 0;
			} catch (error) {
				console.log('Could not load images count:', error);
			}

			setStats({
				totalSlides:
					slidesResult.status === 'fulfilled' ? slidesResult.value?.data?.length || 0 : 0,
				totalImages: imagesCount,
				totalPublications:
					publicationsResult.status === 'fulfilled'
						? publicationsResult.value?.data?.length || 0
						: 0,
				totalPracticeAreas:
					practiceAreasResult.status === 'fulfilled'
						? practiceAreasResult.value?.data?.length || 0
						: 0,
				totalAttorneys:
					attorneysResult.status === 'fulfilled' ? attorneysResult.value?.data?.length || 0 : 0,
			});

			// Check system health
			checkSystemHealth();

			// Load recent activity
			loadRecentActivity();
		} catch (error) {
			console.error('Error loading dashboard data:', error);
		} finally {
			setLoading(false);
		}
	};

	const checkSystemHealth = async () => {
		const health = {
			database: 'unknown',
			storage: 'unknown',
			api: 'unknown',
		};

		// Check database
		try {
			await practiceAreasService.getAllPracticeAreas();
			health.database = 'healthy';
		} catch (error) {
			health.database = 'error';
		}

		// Check storage
		try {
			await storageService.listImages('images');
			health.storage = 'healthy';
		} catch (error) {
			health.storage = 'error';
		}

		// Check API
		try {
			await attorneysService.getAllAttorneys();
			health.api = 'healthy';
		} catch (error) {
			health.api = 'error';
		}

		setSystemHealth(health);
	};

	const loadRecentActivity = () => {
		// This would typically come from a backend service
		const activities = [
			{
				id: 1,
				type: 'success',
				message: 'Database connection established',
				timestamp: new Date().toISOString(),
				icon: 'CheckCircle',
			},
			{
				id: 2,
				type: 'info',
				message: 'Admin dashboard loaded successfully',
				timestamp: new Date(Date.now() - 60000).toISOString(),
				icon: 'Info',
			},
			{
				id: 3,
				type: 'warning',
				message: 'Storage service connection pending',
				timestamp: new Date(Date.now() - 120000).toISOString(),
				icon: 'AlertTriangle',
			},
		];
		setRecentActivity(activities);
	};

	const handleRefresh = () => {
		loadDashboardData();
	};

	const handleBackup = async () => {
		try {
			// This would trigger a backup process
			alert('Backup functionality would be implemented here');
		} catch (error) {
			console.error('Backup failed:', error);
		}
	};

	const getHealthColor = (status) => {
		switch (status) {
			case 'healthy':
				return 'text-green-500';
			case 'error':
				return 'text-red-500';
			default:
				return 'text-yellow-500';
		}
	};

	const getHealthIcon = (status) => {
		switch (status) {
			case 'healthy':
				return 'CheckCircle';
			case 'error':
				return 'XCircle';
			default:
				return 'AlertTriangle';
		}
	};

	const quickActions = [
		{
			title: 'Manage Attorneys',
			description: 'Add, edit, and manage attorney profiles',
			icon: 'Users',
			href: '/admin/attorneys',
			color: 'bg-blue-500',
		},
		{
			title: 'Upload Images',
			description: 'Add new images to your website',
			icon: 'Image',
			href: '/admin/images',
			color: 'bg-green-500',
		},
		{
			title: 'Manage Homepage',
			description: 'Update slider content and layout',
			icon: 'Home',
			href: '/admin/homepage',
			color: 'bg-purple-500',
		},
		{
			title: 'Practice Areas',
			description: 'Manage legal practice areas',
			icon: 'Briefcase',
			href: '/admin/practice-areas',
			color: 'bg-orange-500',
		},
		{
			title: 'Publications',
			description: 'Add and edit publications',
			icon: 'FileText',
			href: '/admin/publications',
			color: 'bg-indigo-500',
		},
		{
			title: 'Company Info',
			description: 'Update company information',
			icon: 'Building',
			href: '/admin/company-info',
			color: 'bg-pink-500',
		},
		{
			title: 'Database Tools',
			description: 'Backup, restore, and manage database',
			icon: 'Database',
			href: '/admin/database',
			color: 'bg-red-500',
		},
		{
			title: 'System Health',
			description: 'Monitor system performance',
			icon: 'Activity',
			href: '/admin/system',
			color: 'bg-teal-500',
		},
	];

	return (
		<div className='space-y-6'>
			{/* Header */}
			<div className='bg-white shadow rounded-lg p-6'>
				<div className='flex justify-between items-center'>
					<div>
						<h1 className='text-2xl font-bold text-gray-900'>Admin Dashboard</h1>
						<p className='text-gray-600'>
							Welcome to your FEPA Law Partners content management system
						</p>
					</div>
					<div className='flex space-x-3'>
						<Button
							variant='default'
							onClick={handleRefresh}
							disabled={loading}
							iconName='RefreshCw'
							iconPosition='left'
							className='bg-[#800020] hover:bg-[#800020]/90'
						>
							{loading ? 'Refreshing...' : 'Refresh Data'}
						</Button>
						<Button
							variant='default'
							onClick={handleBackup}
							iconName='Download'
							iconPosition='left'
							className='bg-[#800020] hover:bg-[#800020]/90'
						>
							Backup Data
						</Button>
					</div>
				</div>
			</div>

			{/* System Health */}
			<div className='bg-white shadow rounded-lg p-6'>
				<h2 className='text-lg font-semibold text-gray-900 mb-4'>System Health</h2>
				<div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
					<div className='flex items-center p-3 border rounded-lg'>
						<Icon
							name={getHealthIcon(systemHealth.database)}
							className={`h-5 w-5 ${getHealthColor(systemHealth.database)} mr-3`}
						/>
						<div>
							<p className='font-medium text-gray-900'>Database</p>
							<p className={`text-sm ${getHealthColor(systemHealth.database)}`}>
								{systemHealth.database.charAt(0).toUpperCase() + systemHealth.database.slice(1)}
							</p>
						</div>
					</div>
					<div className='flex items-center p-3 border rounded-lg'>
						<Icon
							name={getHealthIcon(systemHealth.storage)}
							className={`h-5 w-5 ${getHealthColor(systemHealth.storage)} mr-3`}
						/>
						<div>
							<p className='font-medium text-gray-900'>Storage</p>
							<p className={`text-sm ${getHealthColor(systemHealth.storage)}`}>
								{systemHealth.storage.charAt(0).toUpperCase() + systemHealth.storage.slice(1)}
							</p>
						</div>
					</div>
					<div className='flex items-center p-3 border rounded-lg'>
						<Icon
							name={getHealthIcon(systemHealth.api)}
							className={`h-5 w-5 ${getHealthColor(systemHealth.api)} mr-3`}
						/>
						<div>
							<p className='font-medium text-gray-900'>API</p>
							<p className={`text-sm ${getHealthColor(systemHealth.api)}`}>
								{systemHealth.api.charAt(0).toUpperCase() + systemHealth.api.slice(1)}
							</p>
						</div>
					</div>
				</div>
			</div>

			{/* Stats Grid */}
			<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6'>
				<div className='bg-white shadow rounded-lg p-6'>
					<div className='flex items-center'>
						<div className='flex-shrink-0'>
							<div className='w-8 h-8 bg-[#800020] rounded-md flex items-center justify-center'>
								<Icon name='Image' className='h-5 w-5 text-white' />
							</div>
						</div>
						<div className='ml-4'>
							<p className='text-sm font-medium text-gray-500'>Homepage Slides</p>
							<p className='text-2xl font-semibold text-gray-900'>{stats.totalSlides}</p>
						</div>
					</div>
				</div>

				<div className='bg-white shadow rounded-lg p-6'>
					<div className='flex items-center'>
						<div className='flex-shrink-0'>
							<div className='w-8 h-8 bg-[#DB954E] rounded-md flex items-center justify-center'>
								<Icon name='Image' className='h-5 w-5 text-white' />
							</div>
						</div>
						<div className='ml-4'>
							<p className='text-sm font-medium text-gray-500'>Total Images</p>
							<p className='text-2xl font-semibold text-gray-900'>{stats.totalImages}</p>
						</div>
					</div>
				</div>

				<div className='bg-white shadow rounded-lg p-6'>
					<div className='flex items-center'>
						<div className='flex-shrink-0'>
							<div className='w-8 h-8 bg-green-500 rounded-md flex items-center justify-center'>
								<Icon name='FileText' className='h-5 w-5 text-white' />
							</div>
						</div>
						<div className='ml-4'>
							<p className='text-sm font-medium text-gray-500'>Publications</p>
							<p className='text-2xl font-semibold text-gray-900'>{stats.totalPublications}</p>
						</div>
					</div>
				</div>

				<div className='bg-white shadow rounded-lg p-6'>
					<div className='flex items-center'>
						<div className='flex-shrink-0'>
							<div className='w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center'>
								<Icon name='Briefcase' className='h-5 w-5 text-white' />
							</div>
						</div>
						<div className='ml-4'>
							<p className='text-sm font-medium text-gray-500'>Practice Areas</p>
							<p className='text-2xl font-semibold text-gray-900'>{stats.totalPracticeAreas}</p>
						</div>
					</div>
				</div>

				<div className='bg-white shadow rounded-lg p-6'>
					<div className='flex items-center'>
						<div className='flex-shrink-0'>
							<div className='w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center'>
								<Icon name='Users' className='h-5 w-5 text-white' />
							</div>
						</div>
						<div className='ml-4'>
							<p className='text-sm font-medium text-gray-500'>Attorneys</p>
							<p className='text-2xl font-semibold text-gray-900'>{stats.totalAttorneys}</p>
						</div>
					</div>
				</div>
			</div>

			{/* Quick Actions */}
			<div className='bg-white shadow rounded-lg p-6'>
				<h2 className='text-lg font-semibold text-gray-900 mb-4'>Quick Actions</h2>
				<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
					{quickActions.map((action, index) => (
						<a
							key={index}
							href={action.href}
							className='block p-4 border border-gray-200 rounded-lg hover:border-[#800020] hover:shadow-md transition-all'
						>
							<div className='flex items-center'>
								<div
									className={`w-10 h-10 ${action.color} rounded-lg flex items-center justify-center`}
								>
									<Icon name={action.icon} className='h-6 w-6 text-white' />
								</div>
								<div className='ml-3'>
									<h3 className='text-sm font-medium text-gray-900'>{action.title}</h3>
									<p className='text-xs text-gray-500'>{action.description}</p>
								</div>
							</div>
						</a>
					))}
				</div>
			</div>

			{/* Recent Activity */}
			<div className='bg-white shadow rounded-lg p-6'>
				<h2 className='text-lg font-semibold text-gray-900 mb-4'>Recent Activity</h2>
				<div className='space-y-3'>
					{recentActivity.map((activity) => (
						<div key={activity.id} className='flex items-center text-sm text-gray-600'>
							<Icon
								name={activity.icon}
								className={`h-4 w-4 ${getHealthColor(
									activity.type === 'success'
										? 'healthy'
										: activity.type === 'error'
										? 'error'
										: 'unknown'
								)} mr-2`}
							/>
							<span>{activity.message}</span>
							<span className='ml-auto text-gray-400'>
								{new Date(activity.timestamp).toLocaleTimeString()}
							</span>
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

export default Dashboard;
